import React, { useEffect, useState, useCallback } from "react";
import { Container, Row, Col, Card, Table, Button, Form, Image, Spinner, Alert, InputGroup } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import PageLayout from "../../../layouts/PageLayout";
import axios from "axios";

export default function ServicesListPage() {
    const navigate = useNavigate();
    const [services, setServices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [error, setError] = useState("");
    const [message, setMessage] = useState("");

    const getAuthHeaders = () => ({
        Authorization: localStorage.getItem("authToken") ? `Bearer ${localStorage.getItem("authToken")}` : "",
    });

    const getImageUrl = (path) => {
        if (!path) return "";
        return path.startsWith("http") ? path : `${process.env.REACT_APP_IMG_URL}/${path}`;
    };

    const fetchServices = useCallback(async () => {
        try {
            setLoading(true);
            setError("");
            const response = await axios.get(`${process.env.REACT_APP_API_URL}/web-manager/services`, {
                headers: getAuthHeaders(),
            });
            setServices(response.data?.data || response.data || []);
        } catch (err) {
            setError(err.response?.data?.message || "Failed to load services.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchServices();
    }, [fetchServices]);

    const handleDelete = async (id) => {
        if (!window.confirm("Are you sure you want to delete this service?")) return;

        try {
            setError("");
            await axios.delete(`${process.env.REACT_APP_API_URL}/web-manager/services/${id}`, {
                headers: getAuthHeaders(),
            });
            setMessage("Service deleted successfully.");
            fetchServices();
        } catch (err) {
            setError(err.response?.data?.message || "Failed to delete service.");
        }
    };

    const filteredServices = services.filter((service) =>
        service.title?.toLowerCase().includes(search.toLowerCase()) ||
        service.slug?.toLowerCase().includes(search.toLowerCase())
    );

    return (
        <PageLayout>
            <Container fluid className="py-4">
                <Row className="align-items-center mb-4">
                    <Col>
                        <h2 className="fw-bold mb-1">Services</h2>
                        <p className="text-muted mb-0">Manage all website services and offerings.</p>
                    </Col>
                    <Col xs="auto">
                        <Button variant="primary" className="px-4 fw-semibold" onClick={() => navigate("/services/create")}>
                            + Add New Service
                        </Button>
                    </Col>
                </Row>

                {message && <Alert variant="success" dismissible onClose={() => setMessage("")}>{message}</Alert>}
                {error && <Alert variant="danger" dismissible onClose={() => setError("")}>{error}</Alert>}

                <Card className="border-0 shadow-sm mb-4">
                    <Card.Body className="p-3">
                        <InputGroup>
                            <Form.Control
                                type="text"
                                placeholder="Search services by title or slug..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                className="bg-light border-0 py-2 px-3"
                            />
                            {search && (
                                <Button variant="secondary" onClick={() => setSearch("")}>
                                    Clear
                                </Button>
                            )}
                        </InputGroup>
                    </Card.Body>
                </Card>

                <Card className="border-0 shadow-sm">
                    <Card.Body className="p-0">
                        {loading ? (
                            <div className="text-center py-5">
                                <Spinner animation="border" />
                                <div className="mt-2 text-muted">Loading services...</div>
                            </div>
                        ) : filteredServices.length === 0 ? (
                            <div className="text-center py-5 text-muted">No services found.</div>
                        ) : (
                            <Table responsive hover className="align-middle mb-0">
                                <thead className="bg-light">
                                    <tr>
                                        <th className="ps-4">#</th>
                                        <th>Image</th>
                                        <th>Title & Slug</th>
                                        <th>Description</th>
                                        <th className="text-end pe-4">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredServices.map((service, index) => (
                                        <tr key={service.id || index}>
                                            <td className="ps-4 fw-bold text-muted">{index + 1}</td>
                                            <td>
                                                {service.image ? (
                                                    <Image
                                                        src={getImageUrl(service.image)}
                                                        alt={service.title}
                                                        rounded
                                                        style={{ width: "50px", height: "50px", objectFit: "cover" }}
                                                    />
                                                ) : (
                                                    <span className="badge bg-secondary">No Image</span>
                                                )}
                                            </td>
                                            <td>
                                                <div className="fw-semibold text-dark">{service.title}</div>
                                                <small className="text-muted d-block">{service.slug}</small>
                                            </td>
                                            <td>
                                                <div
                                                    className="text-truncate"
                                                    style={{ maxWidth: "300px" }}
                                                    dangerouslySetInnerHTML={{ __html: service.description || "N/A" }}
                                                />
                                            </td>
                                            <td className="text-end pe-4">
                                                <Button
                                                    variant="primary"
                                                    size="sm"
                                                    className="me-2"
                                                    onClick={() => navigate(`/services/edit/${service.id}`, { state: { service } })}
                                                >
                                                    Edit
                                                </Button>
                                                <Button
                                                    variant="danger"
                                                    size="sm"
                                                    onClick={() => handleDelete(service.id)}
                                                >
                                                    Delete
                                                </Button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        )}
                    </Card.Body>
                </Card>
            </Container>
        </PageLayout>
    );
}